// SPDX-License-Identifier: AGPL-3.0-or-later

import {useFormSubmit} from '@app/features/app/hooks/useFormSubmit';
import * as GuildCommands from '@app/features/guild/commands/GuildCommands';
import styles from '@app/features/guild/components/modals/guild_tabs/GuildDiscoveryTab.module.css';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Form} from '@app/features/ui/components/form/Form';
import {Input, Textarea} from '@app/features/ui/components/form/Input';
import {Select, type SelectOption} from '@app/features/ui/components/form/Select';
import {Spinner} from '@app/features/ui/components/Spinner';
import {getDiscoveryLanguageLabel} from '@app/features/user/utils/LocaleUtils';
import {
	DISCOVERY_DEFAULT_LANGUAGE,
	DISCOVERY_DESCRIPTION_MAX_LENGTH,
	DISCOVERY_DESCRIPTION_MIN_LENGTH,
	DISCOVERY_MAX_TAGS,
	DISCOVERY_TAG_MAX_LENGTH,
	DiscoveryApplicationStatus,
	DiscoverySupportedLanguages,
	isValidDiscoveryTag,
	normalizeDiscoveryTag,
} from '@app/vendor/constants/DiscoveryConstants';
import type {
	DiscoveryApplicationResponse,
	DiscoveryStatusResponse,
} from '@app/vendor/schema/domains/guild/GuildDiscoverySchemas';
import {Trans, useLingui} from '@lingui/react/macro';
import {InfoIcon, WarningIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';

const logger = new Logger('GuildDiscoveryTab');
interface FormInputs {
	description: string;
	category_type: number;
	primary_language: string;
	custom_tags: Array<string>;
}
function StatusBadge({status}: {status: string}) {
	const {t} = useLingui();
	const statusConfig: Record<string, {label: string; className: string}> = useMemo(
		() => ({
			[DiscoveryApplicationStatus.PENDING]: {label: t`Pending`, className: styles.statusPending},
			[DiscoveryApplicationStatus.APPROVED]: {label: t`Approved`, className: styles.statusApproved},
			[DiscoveryApplicationStatus.REJECTED]: {label: t`Rejected`, className: styles.statusRejected},
			[DiscoveryApplicationStatus.REMOVED]: {label: t`Removed`, className: styles.statusRemoved},
		}),
		[t],
	);
	const config = statusConfig[status];
	if (!config) return null;
	return <span className={clsx(styles.statusBadge, config.className)}>{config.label}</span>;
}
const GuildDiscoveryTab: React.FC<{guildId: string}> = ({guildId}) => {
	const {t} = useLingui();
	const [status, setStatus] = useState<DiscoveryStatusResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isWithdrawing, setIsWithdrawing] = useState(false);
	const categoryOptions: ReadonlyArray<SelectOption<number>> = useMemo(
		() => [
			{value: 0, label: t`Gaming`},
			{value: 1, label: t`Music`},
			{value: 2, label: t`Entertainment`},
			{value: 3, label: t`Education`},
			{value: 4, label: t`Science & Technology`},
			{value: 5, label: t`Content Creator`},
			{value: 6, label: t`Anime & Manga`},
			{value: 7, label: t`Movies & TV`},
			{value: 8, label: t`Other`},
		],
		[t],
	);
	const languageOptions: ReadonlyArray<SelectOption<string>> = useMemo(
		() =>
			DiscoverySupportedLanguages.map((lang) => ({
				value: lang.code,
				label: getDiscoveryLanguageLabel(lang.code),
			})),
		[t],
	);
	const [tagInput, setTagInput] = useState('');
	const fetchStatus = useCallback(async () => {
		try {
			setIsLoading(true);
			const data = await GuildCommands.getDiscoveryStatus(guildId);
			setStatus(data);
		} catch (err) {
			logger.error('Failed to fetch discovery status', err);
		} finally {
			setIsLoading(false);
		}
	}, [guildId]);
	useEffect(() => {
		void fetchStatus();
	}, [fetchStatus]);
	const application = status?.application ?? null;
	const eligible = status?.eligible ?? false;
	const minMemberCount = status?.min_member_count ?? 0;
	const hasActiveApplication =
		application != null &&
		(application.status === DiscoveryApplicationStatus.PENDING ||
			application.status === DiscoveryApplicationStatus.APPROVED);
	const canApply =
		!hasActiveApplication &&
		(application == null ||
			application.status === DiscoveryApplicationStatus.REJECTED ||
			application.status === DiscoveryApplicationStatus.REMOVED);
	const formValues = useMemo(
		() =>
			hasActiveApplication && application
				? {
						description: application.description,
						category_type: application.category_type,
						primary_language: application.primary_language ?? DISCOVERY_DEFAULT_LANGUAGE,
						custom_tags: application.custom_tags ?? [],
					}
				: undefined,
		[hasActiveApplication, application],
	);
	const form = useForm<FormInputs>({
		defaultValues: {
			description: '',
			category_type: 0,
			primary_language: DISCOVERY_DEFAULT_LANGUAGE,
			custom_tags: [],
		},
		values: formValues,
	});
	const setApplicationFromResponse = useCallback((response: DiscoveryApplicationResponse) => {
		setStatus((prev) => (prev ? {...prev, application: response} : prev));
	}, []);
	const onSubmit = useCallback(
		async (data: FormInputs) => {
			const payload = {
				description: data.description,
				category_type: data.category_type,
				primary_language: data.primary_language,
				custom_tags: data.custom_tags,
			};
			if (hasActiveApplication) {
				const result = await GuildCommands.updateDiscoveryApplication(guildId, payload);
				setApplicationFromResponse(result);
				form.reset(data);
				ToastCommands.createToast({
					type: 'success',
					children: <Trans>Discovery listing updated</Trans>,
				});
			} else {
				const result = await GuildCommands.applyForDiscovery(guildId, payload);
				setApplicationFromResponse(result);
				form.reset(data);
				ToastCommands.createToast({
					type: 'success',
					children: <Trans>Discovery application submitted</Trans>,
				});
			}
		},
		[guildId, hasActiveApplication, form, setApplicationFromResponse],
	);
	const {handleSubmit, isSubmitting} = useFormSubmit({
		form,
		onSubmit,
		defaultErrorField: 'description',
	});
	const handleWithdraw = useCallback(async () => {
		try {
			setIsWithdrawing(true);
			await GuildCommands.withdrawDiscoveryApplication(guildId);
			setStatus((prev) => (prev ? {...prev, application: null} : prev));
			form.reset({
				description: '',
				category_type: 0,
				primary_language: DISCOVERY_DEFAULT_LANGUAGE,
				custom_tags: [],
			});
			ToastCommands.createToast({
				type: 'success',
				children: <Trans>Discovery application withdrawn</Trans>,
			});
		} catch (err) {
			logger.error('Failed to withdraw discovery application', err);
			ToastCommands.createToast({
				type: 'error',
				children: <Trans>Failed to withdraw application. Please try again.</Trans>,
			});
		} finally {
			setIsWithdrawing(false);
		}
	}, [guildId, form]);
	if (isLoading) {
		return (
			<div className={styles.spinnerContainer}>
				<Spinner />
			</div>
		);
	}
	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h2 className={styles.title}>
					<Trans>Discovery</Trans>
				</h2>
				<p className={styles.subtitle}>
					<Trans>List your community in Discovery so others can find and join it.</Trans>
				</p>
			</div>
			{!eligible && canApply && (
				<div className={styles.warning}>
					<div className={styles.warningContent}>
						<div className={styles.warningIcon}>
							<WarningIcon size={20} weight="fill" />
						</div>
						<div className={styles.warningBody}>
							<p className={styles.warningTitle}>
								<Trans>Not enough members</Trans>
							</p>
							<p className={styles.warningText}>
								<Trans>
									Your community needs at least {minMemberCount} members before it can be listed in Discovery.
								</Trans>
							</p>
						</div>
					</div>
				</div>
			)}
			{application != null && (
				<div className={styles.statusCard}>
					<div className={styles.statusRow}>
						<span className={styles.statusLabel}>
							<Trans>Status:</Trans>
						</span>
						<StatusBadge status={application.status} />
					</div>
					{(application.removal_reason ?? application.review_reason) && (
						<p className={styles.reviewReason}>
							<Trans>Reason: {application.removal_reason ?? application.review_reason}</Trans>
						</p>
					)}
				</div>
			)}
			{application?.status === DiscoveryApplicationStatus.APPROVED && (
				<div className={styles.info}>
					<div className={styles.infoContent}>
						<div className={styles.infoIcon}>
							<InfoIcon size={20} weight="fill" />
						</div>
						<p className={styles.infoText}>
							<Trans>
								Your community is listed in Discovery. You can update your listing details below or withdraw to remove
								it.
							</Trans>
						</p>
					</div>
				</div>
			)}
			{application?.status === DiscoveryApplicationStatus.PENDING && (
				<div className={styles.info}>
					<div className={styles.infoContent}>
						<div className={styles.infoIcon}>
							<InfoIcon size={20} weight="fill" />
						</div>
						<p className={styles.infoText}>
							<Trans>
								Your application is pending review. You can still update your listing details or withdraw the
								application.
							</Trans>
						</p>
					</div>
				</div>
			)}
			{(canApply || hasActiveApplication) && (
				<Form form={form} onSubmit={handleSubmit}>
					<div className={styles.formCard}>
						<div>
							<div className={styles.fieldLabel}>
								<Trans>Description</Trans>
							</div>
							<Controller
								name="description"
								control={form.control}
								rules={{
									required: t`A description is required.`,
									minLength: {
										value: DISCOVERY_DESCRIPTION_MIN_LENGTH,
										message: t`Description must be at least ${DISCOVERY_DESCRIPTION_MIN_LENGTH} characters.`,
									},
									maxLength: {
										value: DISCOVERY_DESCRIPTION_MAX_LENGTH,
										message: t`Description must be no more than ${DISCOVERY_DESCRIPTION_MAX_LENGTH} characters.`,
									},
								}}
								render={({field, fieldState}) => (
									<Textarea
										name={field.name}
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										ref={field.ref}
										error={fieldState.error?.message}
										label=""
										placeholder={t`Describe what your community is about...`}
										minRows={3}
										maxRows={6}
										maxLength={DISCOVERY_DESCRIPTION_MAX_LENGTH}
										showCharacterCount
										disabled={!eligible && canApply}
									/>
								)}
							/>
						</div>
						<div>
							<div className={styles.fieldLabel}>
								<Trans>Category</Trans>
							</div>
							<Controller
								name="category_type"
								control={form.control}
								render={({field}) => (
									<Select<number>
										value={field.value}
										onChange={field.onChange}
										options={categoryOptions}
										isSearchable={false}
										disabled={!eligible && canApply}
									/>
								)}
							/>
							<p className={styles.helpText}>
								<Trans>Choose the category that best describes your community. You can change this any time.</Trans>
							</p>
						</div>
						<div>
							<div className={styles.fieldLabel}>
								<Trans>Primary language</Trans>
							</div>
							<Controller
								name="primary_language"
								control={form.control}
								render={({field}) => (
									<Select<string>
										value={field.value}
										onChange={field.onChange}
										options={languageOptions}
										isSearchable
										disabled={!eligible && canApply}
									/>
								)}
							/>
							<p className={styles.helpText}>
								<Trans>The language most of your community speaks. Used to filter discovery results.</Trans>
							</p>
						</div>
						<div>
							<div className={styles.fieldLabel}>
								<Trans>Custom tags</Trans>
							</div>
							<Controller
								name="custom_tags"
								control={form.control}
								render={({field}) => {
									const tags: Array<string> = field.value ?? [];
									const addTag = (rawValue: string) => {
										const value = normalizeDiscoveryTag(rawValue);
										if (!value) return;
										if (!isValidDiscoveryTag(value)) {
											ToastCommands.createToast({
												type: 'error',
												children: <Trans>Tags must be 2–{DISCOVERY_TAG_MAX_LENGTH} characters and alphanumeric.</Trans>,
											});
											return;
										}
										if (tags.includes(value)) return;
										if (tags.length >= DISCOVERY_MAX_TAGS) {
											ToastCommands.createToast({
												type: 'error',
												children: <Trans>You can only add up to {DISCOVERY_MAX_TAGS} tags.</Trans>,
											});
											return;
										}
										field.onChange([...tags, value]);
										setTagInput('');
									};
									const removeTag = (tag: string) => {
										field.onChange(tags.filter((t) => t !== tag));
									};
									return (
										<div className={styles.tagsField}>
											<div className={styles.tagsList}>
												{tags.map((tag) => (
													<span key={tag} className={styles.tagChip}>
														{tag}
														<button
															type="button"
															aria-label={t`Remove tag ${tag}`}
															className={styles.tagChipRemove}
															onClick={() => removeTag(tag)}
															disabled={!eligible && canApply}
														>
															×
														</button>
													</span>
												))}
											</div>
											<div className={styles.tagsInputRow}>
												<Input
													value={tagInput}
													onChange={(e) => setTagInput(e.target.value)}
													onKeyDown={(e) => {
														if (e.key === 'Enter' || e.key === ',') {
															e.preventDefault();
															addTag(tagInput);
														} else if (e.key === 'Backspace' && tagInput.length === 0 && tags.length > 0) {
															removeTag(tags[tags.length - 1]!);
														}
													}}
													maxLength={DISCOVERY_TAG_MAX_LENGTH}
													placeholder={t`Add a tag and press Enter…`}
													disabled={(!eligible && canApply) || tags.length >= DISCOVERY_MAX_TAGS}
												/>
												<Button
													type="button"
													small
													variant="secondary"
													onClick={() => addTag(tagInput)}
													disabled={
														(!eligible && canApply) || tags.length >= DISCOVERY_MAX_TAGS || tagInput.trim().length === 0
													}
												>
													<Trans>Add</Trans>
												</Button>
											</div>
										</div>
									);
								}}
							/>
							<p className={styles.helpText}>
								<Trans>
									Up to {DISCOVERY_MAX_TAGS} tags help people find your community. They show up in discovery search.
								</Trans>
							</p>
						</div>
						<div className={styles.actions}>
							{hasActiveApplication && (
								<Button type="button" variant="danger-secondary" onClick={handleWithdraw} submitting={isWithdrawing}>
									<Trans>Withdraw</Trans>
								</Button>
							)}
							<Button type="submit" submitting={isSubmitting} disabled={!eligible && canApply}>
								{hasActiveApplication ? <Trans>Save</Trans> : <Trans>Apply</Trans>}
							</Button>
						</div>
					</div>
				</Form>
			)}
		</div>
	);
};

export default GuildDiscoveryTab;
